import { bfLevert, deriveBfTarget, type BfTargetSalesUnit, type DashboardOwner, type DashboardProject } from "./dashboard-bf-calc";
import { type DashboardPeriod, periodContains } from "./dashboard-period";
import { isSalesProject, pickClosedDate } from "./offer-closed-date";

export interface SalesDisplayMetric {
  annualTargetKr: number;
  achievedGeekKr: number;
  achievementPct: number | null;
  monthSalesKr: number;
  monthEstimatedGeekKr: number;
  monthDealCount: number;
  averageSaleKr: number;
}

export interface SalesUnitDisplayMetric extends SalesDisplayMetric {
  unitId: string;
  unitName: string;
  memberUserIds: string[];
}

export interface SalesDisplayMetrics {
  team: SalesDisplayMetric;
  units: SalesUnitDisplayMetric[];
}

function percentage(value: number, target: number): number | null {
  return target > 0 ? Math.round((value / target) * 1000) / 10 : null;
}

function sumUnitShare(
  eligible: DashboardProject[],
  owners: DashboardOwner[],
  unitId: string,
  valueOf: (project: DashboardProject) => number,
): number {
  const projectsById = new Map(eligible.map((project) => [project.id, project]));
  return Math.round(owners.reduce((sum, owner) => {
    if (owner.sales_unit_id !== unitId) return sum;
    const project = projectsById.get(owner.project_id);
    if (!project) return sum;
    return sum + valueOf(project) * (Number(owner.ownership_pct ?? 0) / 100);
  }, 0));
}

function monthlyProjects(projects: DashboardProject[], monthPeriod: DashboardPeriod): DashboardProject[] {
  return projects.filter((project) =>
    isSalesProject(project) && periodContains(monthPeriod, pickClosedDate(project, project.offer_accepted_at)),
  );
}

function unitDealCount(eligible: DashboardProject[], owners: DashboardOwner[], unitId: string): number {
  const eligibleIds = new Set(eligible.map((project) => project.id));
  return new Set(
    owners
      .filter((owner) => owner.sales_unit_id === unitId && eligibleIds.has(owner.project_id))
      .map((owner) => owner.project_id),
  ).size;
}

/** Fixed display metrics: signed sales for this month and realised GEEK for this year. */
export function calcSalesDisplayMetrics(
  projects: DashboardProject[],
  owners: DashboardOwner[],
  salesUnits: BfTargetSalesUnit[],
  yearPeriod: DashboardPeriod,
  monthPeriod: DashboardPeriod,
): SalesDisplayMetrics {
  const monthProjects = monthlyProjects(projects, monthPeriod);
  const yearUnits = salesUnits
    .filter((unit) => unit.year === yearPeriod.year)
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "nb"));
  const teamTarget = deriveBfTarget({ salesUnits: yearUnits, period: yearPeriod });
  const teamAchieved = bfLevert(projects, owners, yearPeriod);
  const teamSales = Math.round(monthProjects.reduce((sum, project) => sum + Number(project.total_amount ?? 0), 0));
  const teamEstimatedGeek = Math.round(monthProjects.reduce((sum, project) => sum + Number(project.bf_estimated ?? 0), 0));
  const teamDealCount = monthProjects.length;

  const units = yearUnits.map((unit): SalesUnitDisplayMetric => {
    const target = deriveBfTarget({ salesUnits: [unit], period: yearPeriod });
    const achieved = sumUnitShare(
      projects.filter((project) =>
        isSalesProject(project) && project.completed_at != null && periodContains(yearPeriod, project.completed_at),
      ),
      owners,
      unit.id,
      (project) => Number(project.bf_actual ?? 0),
    );
    const sales = sumUnitShare(monthProjects, owners, unit.id, (project) => Number(project.total_amount ?? 0));
    const estimatedGeek = sumUnitShare(monthProjects, owners, unit.id, (project) => Number(project.bf_estimated ?? 0));
    const dealCount = unitDealCount(monthProjects, owners, unit.id);
    return {
      unitId: unit.id,
      unitName: unit.name ?? "Selger",
      memberUserIds: unit.memberUserIds,
      annualTargetKr: target,
      achievedGeekKr: achieved,
      achievementPct: percentage(achieved, target),
      monthSalesKr: sales,
      monthEstimatedGeekKr: estimatedGeek,
      monthDealCount: dealCount,
      averageSaleKr: dealCount > 0 ? Math.round(sales / dealCount) : 0,
    };
  });

  return {
    team: {
      annualTargetKr: teamTarget,
      achievedGeekKr: teamAchieved,
      achievementPct: percentage(teamAchieved, teamTarget),
      monthSalesKr: teamSales,
      monthEstimatedGeekKr: teamEstimatedGeek,
      monthDealCount: teamDealCount,
      averageSaleKr: teamDealCount > 0 ? Math.round(teamSales / teamDealCount) : 0,
    },
    units,
  };
}
